import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  RadioGroup,
  Radio,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Backup as BackupIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudUpload as CloudUploadIcon,
  GetApp as GetAppIcon,
  Warning as WarningIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Palette as PaletteIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { settingsAPI, proposalsAPI } from '../../services/api';

// Appearance Settings Component
const AppearanceSettings = () => {
  const { theme, isDarkMode, toggleTheme, setThemeMode, themeMode } = useTheme();
  const [selectedTheme, setSelectedTheme] = useState(themeMode);

  const handleThemeChange = (event) => {
    const newThemeMode = event.target.value;
    setSelectedTheme(newThemeMode);
    setThemeMode(newThemeMode);
    toast.success(`Theme changed to ${newThemeMode} mode`);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Appearance Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Customize the look and feel of your application.
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PaletteIcon sx={{ mr: 1 }} /> Theme Mode
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="theme-mode"
                  name="theme-mode"
                  value={selectedTheme}
                  onChange={handleThemeChange}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    gap: 2, 
                    width: '100%' 
                  }}>
                    <Card 
                      sx={{
                        flex: 1,
                        p: 2,
                        border: selectedTheme === 'light' ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                        cursor: 'pointer',
                        backgroundColor: '#ffffff',
                        color: '#000000',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                      onClick={() => setThemeMode('light')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <FormControlLabel 
                          value="light" 
                          control={<Radio />} 
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Brightness7Icon sx={{ mr: 1, color: '#f9a825' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000000' }}>
                                Light Mode
                              </Typography>
                            </Box>
                          } 
                        />
                      </Box>
                      <Box sx={{ 
                        height: 120, 
                        display: 'flex', 
                        flexDirection: 'column',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ height: '30%', backgroundColor: '#1976d2', p: 1 }}>
                          <Box sx={{ width: '40%', height: 8, backgroundColor: '#ffffff', borderRadius: 1 }} />
                        </Box>
                        <Box sx={{ p: 1, flex: 1 }}>
                          <Box sx={{ width: '90%', height: 8, backgroundColor: '#e0e0e0', borderRadius: 1, mb: 1 }} />
                          <Box sx={{ width: '70%', height: 8, backgroundColor: '#e0e0e0', borderRadius: 1 }} />
                        </Box>
                      </Box>
                    </Card>

                    <Card 
                      sx={{
                        flex: 1,
                        p: 2,
                        border: selectedTheme === 'dark' ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                        cursor: 'pointer',
                        backgroundColor: '#121212',
                        color: '#ffffff',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                      onClick={() => setThemeMode('dark')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <FormControlLabel 
                          value="dark" 
                          control={<Radio />} 
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Brightness4Icon sx={{ mr: 1, color: '#90caf9' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#ffffff' }}>
                                Dark Mode
                              </Typography>
                            </Box>
                          } 
                        />
                      </Box>
                      <Box sx={{ 
                        height: 120, 
                        display: 'flex', 
                        flexDirection: 'column',
                        backgroundColor: '#1e1e1e',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ height: '30%', backgroundColor: '#90caf9', p: 1 }}>
                          <Box sx={{ width: '40%', height: 8, backgroundColor: '#1e1e1e', borderRadius: 1 }} />
                        </Box>
                        <Box sx={{ p: 1, flex: 1 }}>
                          <Box sx={{ width: '90%', height: 8, backgroundColor: '#333333', borderRadius: 1, mb: 1 }} />
                          <Box sx={{ width: '70%', height: 8, backgroundColor: '#333333', borderRadius: 1 }} />
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                </RadioGroup>
              </FormControl>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Select your preferred theme mode. The theme will be applied immediately across the entire application.
                </Typography>
              </Box>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                  onClick={toggleTheme}
                >
                  Toggle to {isDarkMode ? 'Light' : 'Dark'} Mode
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Main Settings Component
const Settings = () => {
  const [tabValue, setTabValue] = useState(0);
  const { settings, updateSettings } = useSettings();
  const { user } = useAuth();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          aria-label="settings tabs"
        >
          <Tab label="Company" icon={<SaveIcon />} />
          <Tab label="Email" icon={<EmailIcon />} />
          <Tab label="Appearance" icon={<PaletteIcon />} />
          <Tab label="Notifications" icon={<NotificationsIcon />} />
          <Tab label="Security" icon={<SecurityIcon />} />
          <Tab label="Backup" icon={<BackupIcon />} />
        </Tabs>
      </Paper>

      {/* Company Settings */}
      <TabPanel value={tabValue} index={0}>
        <CompanySettings />
      </TabPanel>

      {/* Email Settings */}
      <TabPanel value={tabValue} index={1}>
        <EmailSettings />
      </TabPanel>

      {/* Appearance Settings */}
      <TabPanel value={tabValue} index={2}>
        <AppearanceSettings />
      </TabPanel>

      {/* Notification Settings */}
      <TabPanel value={tabValue} index={3}>
        <NotificationSettings />
      </TabPanel>

      {/* Security Settings */}
      <TabPanel value={tabValue} index={4}>
        <SecuritySettings />
      </TabPanel>

      {/* Backup & Restore Settings */}
      <TabPanel value={tabValue} index={5}>
        <BackupSettings />
      </TabPanel>
    </Box>
  );
};

// TabPanel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Company Settings Component
const CompanySettings = () => {
  const { settings, updateCompanyInfo, uploadLogo, removeLogo, getLogoUrl } = useSettings();
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const companySchema = yup.object({
    name: yup.string().required('Company name is required'),
    email: yup.string().email('Invalid email format').required('Email is required'),
    phone: yup.string(),
    website: yup.string().url('Invalid URL format'),
    address: yup.string(),
    city: yup.string(),
    state: yup.string(),
    zipCode: yup.string(),
    country: yup.string(),
    taxId: yup.string()
  });
  
  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: yupResolver(companySchema),
    defaultValues: {
      name: settings.company.name,
      email: settings.company.email,
      phone: settings.company.phone,
      website: settings.company.website,
      address: settings.company.address,
      city: settings.company.city,
      state: settings.company.state,
      zipCode: settings.company.zipCode,
      country: settings.company.country,
      taxId: settings.company.taxId
    }
  });
  
  // Update form when settings change
  useEffect(() => {
    reset({
      name: settings.company.name,
      email: settings.company.email,
      phone: settings.company.phone,
      website: settings.company.website,
      address: settings.company.address,
      city: settings.company.city,
      state: settings.company.state,
      zipCode: settings.company.zipCode,
      country: settings.company.country,
      taxId: settings.company.taxId
    });
    
    // Set logo preview if exists
    if (settings.companyLogo) {
      setLogoPreview(getLogoUrl());
    }
  }, [settings, reset, getLogoUrl]);
  
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await updateCompanyInfo(data);
      if (result.success) {
        toast.success('Company information updated successfully');
      } else {
        toast.error(result.error || 'Failed to update company information');
      }
    } catch (error) {
      toast.error('An error occurred while updating company information');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };
  
  const handleLogoUpload = async () => {
    if (!logoFile) return;
    
    setIsUploading(true);
    try {
      const result = await uploadLogo(logoFile);
      if (result.success) {
        toast.success('Logo uploaded successfully');
        setLogoFile(null);
        // The logo preview will be updated automatically through the useEffect
        // when settings are updated by the uploadLogo function
      } else {
        toast.error(result.error || 'Failed to upload logo');
      }
    } catch (error) {
      toast.error('An error occurred while uploading logo');
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleLogoRemove = async () => {
    setIsUploading(true);
    try {
      const result = await removeLogo();
      if (result.success) {
        toast.success('Logo removed successfully');
        setLogoPreview(null);
        setShowDeleteConfirm(false);
      } else {
        toast.error(result.error || 'Failed to remove logo');
      }
    } catch (error) {
      toast.error('An error occurred while removing logo');
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Company Information
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Update your company details and logo. This information will appear on your proposals and communications.
        </Typography>
      </Grid>
      
      {/* Logo Upload Section */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Company Logo
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
              {logoPreview ? (
                <Box 
                  component="img" 
                  src={logoPreview} 
                  alt="Company Logo" 
                  sx={{ 
                    maxWidth: '100%', 
                    maxHeight: 150, 
                    mb: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 1
                  }} 
                />
              ) : (
                <Box 
                  sx={{ 
                    width: '100%', 
                    height: 150, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                    mb: 2
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No logo uploaded
                  </Typography>
                </Box>
              )}
              
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="logo-upload"
                type="file"
                onChange={handleLogoChange}
              />
              
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <label htmlFor="logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={isUploading}
                  >
                    Select File
                  </Button>
                </label>
                
                {logoFile && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    onClick={handleLogoUpload}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                )}
                
                {logoPreview && !logoFile && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isUploading}
                  >
                    Remove
                  </Button>
                )}
              </Box>
              
              <Dialog
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
              >
                <DialogTitle>Confirm Logo Removal</DialogTitle>
                <DialogContent>
                  <Typography>
                    Are you sure you want to remove your company logo?
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleLogoRemove} color="error" autoFocus>
                    Remove
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      {/* Company Details Form */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Company Details
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Company Name"
                        fullWidth
                        required
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email"
                        fullWidth
                        required
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone"
                        fullWidth
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="website"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Website"
                        fullWidth
                        error={!!errors.website}
                        helperText={errors.website?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="address"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Address"
                        fullWidth
                        multiline
                        rows={2}
                        error={!!errors.address}
                        helperText={errors.address?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="City"
                        fullWidth
                        error={!!errors.city}
                        helperText={errors.city?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="State/Province"
                        fullWidth
                        error={!!errors.state}
                        helperText={errors.state?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="zipCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Postal/Zip Code"
                        fullWidth
                        error={!!errors.zipCode}
                        helperText={errors.zipCode?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Country"
                        fullWidth
                        error={!!errors.country}
                        helperText={errors.country?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="taxId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Tax ID / VAT Number"
                        fullWidth
                        error={!!errors.taxId}
                        helperText={errors.taxId?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Email Settings Component
const EmailSettings = () => {
  const { settings, updateSmtpSettings, testSmtpConfig } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const emailSchema = yup.object({
    smtpHost: yup.string().required('SMTP Host is required'),
    smtpPort: yup.number().required('SMTP Port is required').positive().integer(),
    smtpUser: yup.string().required('SMTP Username is required'),
    smtpPassword: yup.string(),
    smtpSecure: yup.boolean(),
    fromEmail: yup.string().email('Invalid email format').required('From Email is required'),
    fromName: yup.string().required('From Name is required')
  });
  
  const { control, handleSubmit, formState: { errors }, reset, getValues } = useForm({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      smtpHost: settings.email.smtpHost,
      smtpPort: settings.email.smtpPort,
      smtpUser: settings.email.smtpUser,
      smtpPassword: '',
      smtpSecure: settings.email.smtpSecure,
      fromEmail: settings.email.fromEmail,
      fromName: settings.email.fromName
    }
  });
  
  // Update form when settings change
  useEffect(() => {
    reset({
      smtpHost: settings.email.smtpHost,
      smtpPort: settings.email.smtpPort,
      smtpUser: settings.email.smtpUser,
      smtpPassword: '',
      smtpSecure: settings.email.smtpSecure,
      fromEmail: settings.email.fromEmail,
      fromName: settings.email.fromName
    });
  }, [settings, reset]);
  
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await updateSmtpSettings(data);
      if (result.success) {
        toast.success('Email settings updated successfully');
      } else {
        toast.error(result.error || 'Failed to update email settings');
      }
    } catch (error) {
      toast.error('An error occurred while updating email settings');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleTestEmail = async () => {
    const data = getValues();
    setIsTesting(true);
    try {
      // First test SMTP connection
      const smtpResult = await testSmtpConfig(data);
      if (!smtpResult.success) {
        toast.error(smtpResult.error || 'SMTP configuration test failed');
        return;
      }
      
      // Then send actual test email
      const testEmailData = {
        to: data.fromEmail, // Send test email to the configured from email
        subject: 'Test Email from Proposal Pro',
        message: 'This is a test email to verify your SMTP settings are working correctly. If you receive this email, your email configuration is properly set up.'
      };
      
      const response = await settingsAPI.testEmailSettings(testEmailData);
      toast.success('Test email sent successfully! Check your inbox.');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send test email';
      toast.error(errorMessage);
      console.error('Test email error:', error);
    } finally {
      setIsTesting(false);
    }
  };
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Email Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure your email server settings for sending proposals and notifications.
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              SMTP Configuration
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Controller
                    name="smtpHost"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="SMTP Host"
                        fullWidth
                        required
                        error={!!errors.smtpHost}
                        helperText={errors.smtpHost?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Controller
                    name="smtpPort"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="SMTP Port"
                        fullWidth
                        required
                        type="number"
                        error={!!errors.smtpPort}
                        helperText={errors.smtpPort?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtpUser"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="SMTP Username"
                        fullWidth
                        required
                        error={!!errors.smtpUser}
                        helperText={errors.smtpUser?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="smtpPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="SMTP Password"
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        error={!!errors.smtpPassword}
                        helperText={errors.smtpPassword?.message || 'Leave blank to keep current password'}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="smtpSecure"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Use Secure Connection (SSL/TLS)"
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Email Sender Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="fromEmail"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="From Email"
                        fullWidth
                        required
                        error={!!errors.fromEmail}
                        helperText={errors.fromEmail?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="fromName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="From Name"
                        fullWidth
                        required
                        error={!!errors.fromName}
                        helperText={errors.fromName?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<EmailIcon />}
                      onClick={handleTestEmail}
                      disabled={isTesting || isSubmitting}
                    >
                      {isTesting ? 'Sending...' : 'Send Test Email'}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Notification Settings Component
const NotificationSettings = () => {
  return (
    <Alert severity="info">
      Notification settings will be available in a future update.
    </Alert>
  );
};

// Security Settings Component
const SecuritySettings = () => {
  const { settings, updateApplicationSettings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingProposalSettings, setIsSubmittingProposalSettings] = useState(false);
  
  const securitySchema = yup.object({
    sessionTimeoutMinutes: yup.number().required('Session timeout is required').positive().integer(),
    maxLoginAttempts: yup.number().required('Max login attempts is required').positive().integer()
  });
  
  const proposalSecuritySchema = yup.object({
    allowDeleteAnyStatus: yup.boolean()
  });
  
  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: yupResolver(securitySchema),
    defaultValues: {
      sessionTimeoutMinutes: settings.sessionTimeoutMinutes,
      maxLoginAttempts: settings.maxLoginAttempts
    }
  });
  
  const { control: proposalControl, handleSubmit: handleProposalSubmit, formState: { errors: proposalErrors }, reset: resetProposal } = useForm({
    resolver: yupResolver(proposalSecuritySchema),
    defaultValues: {
      allowDeleteAnyStatus: settings.allowDeleteAnyStatus
    }
  });
  
  // Update form when settings change
  useEffect(() => {
    reset({
      sessionTimeoutMinutes: settings.sessionTimeoutMinutes,
      maxLoginAttempts: settings.maxLoginAttempts
    });
    
    resetProposal({
      allowDeleteAnyStatus: settings.allowDeleteAnyStatus
    });
  }, [settings, reset, resetProposal]);
  
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await updateApplicationSettings({
        sessionTimeoutMinutes: data.sessionTimeoutMinutes,
        maxLoginAttempts: data.maxLoginAttempts
      });
      if (result.success) {
        toast.success('Security settings updated successfully');
      } else {
        toast.error(result.error || 'Failed to update security settings');
      }
    } catch (error) {
      toast.error('An error occurred while updating security settings');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const onProposalSubmit = async (data) => {
    setIsSubmittingProposalSettings(true);
    try {
      const result = await updateApplicationSettings({
        allowDeleteAnyStatus: data.allowDeleteAnyStatus
      });
      if (result.success) {
        toast.success('Proposal security settings updated successfully');
      } else {
        toast.error(result.error || 'Failed to update proposal security settings');
      }
    } catch (error) {
      toast.error('An error occurred while updating proposal security settings');
      console.error(error);
    } finally {
      setIsSubmittingProposalSettings(false);
    }
  };
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Security Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure security settings for your application.
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Session & Login Security
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="sessionTimeoutMinutes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Session Timeout (minutes)"
                        fullWidth
                        required
                        type="number"
                        error={!!errors.sessionTimeoutMinutes}
                        helperText={errors.sessionTimeoutMinutes?.message || 'Time before users are automatically logged out due to inactivity'}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Controller
                    name="maxLoginAttempts"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Max Login Attempts"
                        fullWidth
                        required
                        type="number"
                        error={!!errors.maxLoginAttempts}
                        helperText={errors.maxLoginAttempts?.message || 'Number of failed login attempts before account is temporarily locked'}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Proposal Deletion Security
            </Typography>
            <form onSubmit={handleProposalSubmit(onProposalSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="allowDeleteAnyStatus"
                    control={proposalControl}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1">Allow Proposal Deletion in Any Status</Typography>
                            <Typography variant="body2" color="text.secondary">
                              When enabled, proposals can be deleted regardless of their status. Otherwise, only draft proposals can be deleted.
                            </Typography>
                          </Box>
                        }
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      disabled={isSubmittingProposalSettings}
                    >
                      {isSubmittingProposalSettings ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon color="warning" sx={{ mr: 1 }} /> Password Policy
            </Typography>
            <Typography variant="body2" paragraph>
              Password requirements for all users:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Minimum 8 characters" />
              </ListItem>
              <ListItem>
                <ListItemText primary="At least one uppercase letter" />
              </ListItem>
              <ListItem>
                <ListItemText primary="At least one lowercase letter" />
              </ListItem>
              <ListItem>
                <ListItemText primary="At least one number" />
              </ListItem>
              <ListItem>
                <ListItemText primary="At least one special character" />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              Password policy settings will be configurable in a future update.
            </Alert>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Backup Settings Component
const BackupSettings = () => {
  const { exportSettings, importSettings } = useSettings();
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [backupFile, setBackupFile] = useState(null);
  
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await exportSettings();
      if (!result.success) {
        toast.error(result.error || 'Failed to export settings');
      }
    } catch (error) {
      toast.error('An error occurred while exporting settings');
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBackupFile(file);
    }
  };
  
  const handleImport = async () => {
    if (!backupFile) return;
    
    setIsImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const backupData = JSON.parse(e.target.result);
          const result = await importSettings(backupData);
          
          if (result.success) {
            toast.success('Settings imported successfully');
            setBackupFile(null);
            // Reset file input
            document.getElementById('backup-upload').value = '';
          } else {
            toast.error(result.error || 'Failed to import settings');
          }
        } catch (parseError) {
          toast.error('Invalid backup file format');
          console.error(parseError);
        } finally {
          setIsImporting(false);
        }
      };
      
      reader.onerror = () => {
        toast.error('Failed to read backup file');
        setIsImporting(false);
      };
      
      reader.readAsText(backupFile);
    } catch (error) {
      toast.error('An error occurred while importing settings');
      console.error(error);
      setIsImporting(false);
    }
  };
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Backup & Restore
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Export your settings for backup or import previously exported settings.
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Export Settings
            </Typography>
            <Typography variant="body2" paragraph>
              Download a backup of your current settings. This file can be used to restore your settings later.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetAppIcon />}
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export Settings'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Import Settings
            </Typography>
            <Typography variant="body2" paragraph>
              Restore settings from a previously exported backup file.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <input
                accept=".json"
                style={{ display: 'none' }}
                id="backup-upload"
                type="file"
                onChange={handleFileChange}
              />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <label htmlFor="backup-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={isImporting}
                  >
                    Select Backup File
                  </Button>
                </label>
                
                {backupFile && (
                  <Typography variant="body2">
                    {backupFile.name}
                  </Typography>
                )}
              </Box>
              
              {backupFile && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    onClick={handleImport}
                    disabled={isImporting}
                  >
                    {isImporting ? 'Importing...' : 'Import Settings'}
                  </Button>
                </Box>
              )}
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                Importing settings will overwrite your current settings. This action cannot be undone.
              </Alert>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default Settings;